import type { ScenariosRequest } from '@api/api/scenario';
import { deepEqual } from '@helpers/utils/equal/deepEqual';
import { useAssetsFilterEntityTags } from '@terminal/react-query-hooks/useAssetsFilterEntityTags';
import { useScenarioAssistant } from '@terminal/widgets/scenario/components/scenario-assistant-modal/use-scenario-assistant/useScenarioAssistant';
import { createScenarioFallbackSnapshot } from '@terminal/widgets/scenario/hooks/use-scenario-store/scenario-fallback';
import { useScenarioStore } from '@terminal/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import { useEffect } from 'react';

import {
  hasAppliedScenarioChanged,
  initializeScenarioFallback,
} from './scenarioInitialization.utils';

interface UseScenarioInitializationProps {
  abortScenarioRequest: () => void;
  sendMessage: (body: ScenariosRequest) => Promise<void>;
  shouldInitializeFallback: boolean;
}

export const useScenarioInitialization = ({
  abortScenarioRequest,
  sendMessage,
  shouldInitializeFallback,
}: UseScenarioInitializationProps) => {
  const {
    dismissScenarioFallback,
    filterGroups,
    scenarioFallback,
    scenarioPageEntityTags,
    setScenarioPageEntityTags,
  } = useScenarioStore(state => ({
    dismissScenarioFallback: state.dismissScenarioFallback,
    filterGroups: state.filterGroups,
    scenarioFallback: state.scenarioFallback,
    scenarioPageEntityTags: state.scenarioPageEntityTags,
    setScenarioPageEntityTags: state.setScenarioPageEntityTags,
  }));
  const hasAssistantConditions = useScenarioAssistant(
    state => state.conditions.length > 0
  );
  const {
    data: resolvedAssetFilterEntityTags,
    isLoading: isAssetFilterEntityTagsLoading,
  } = useAssetsFilterEntityTags(filterGroups);
  const serializedAssetFilterEntityTags = JSON.stringify(
    resolvedAssetFilterEntityTags
  );

  useEffect(() => {
    if (!shouldInitializeFallback) {
      return;
    }

    let isInitializationActive = true;

    initializeScenarioFallback({
      hasAssistantConditions: () =>
        useScenarioAssistant.getState().conditions.length > 0,
      isInitializationActive: () => isInitializationActive,
      sendMessage,
    }).catch(() => undefined);

    return () => {
      isInitializationActive = false;
    };
  }, [sendMessage, shouldInitializeFallback]);

  useEffect(() => {
    if (isAssetFilterEntityTagsLoading) {
      return;
    }

    const nextEntityTags = JSON.parse(
      serializedAssetFilterEntityTags
    ) as string[];
    const fallbackState = useScenarioStore.getState();
    const appliedFallback =
      scenarioFallback.status === 'applied' ? scenarioFallback : undefined;
    const matchesFallbackAssetFilter = deepEqual(
      fallbackState.filterGroups.ASSET_FILTERS.activeFilterOptions,
      appliedFallback?.snapshot.ASSET_FILTERS
    );
    if (appliedFallback && matchesFallbackAssetFilter) {
      return;
    }

    setScenarioPageEntityTags(nextEntityTags);
  }, [
    isAssetFilterEntityTagsLoading,
    scenarioFallback,
    serializedAssetFilterEntityTags,
    setScenarioPageEntityTags,
  ]);

  useEffect(() => {
    if (scenarioFallback.status !== 'applied') {
      return;
    }

    const currentSnapshot = createScenarioFallbackSnapshot({
      filterGroups,
      entityTags: scenarioPageEntityTags,
    });

    if (
      hasAppliedScenarioChanged({
        currentSnapshot,
        fallbackSnapshot: scenarioFallback.snapshot,
        hasAssistantConditions,
      })
    ) {
      abortScenarioRequest();
      dismissScenarioFallback();
    }
  }, [
    abortScenarioRequest,
    dismissScenarioFallback,
    filterGroups,
    hasAssistantConditions,
    scenarioFallback,
    scenarioPageEntityTags,
  ]);

  return {
    scenarioFallback,
    scenarioPageEntityTags,
  };
};
